import type { CSSObject } from '@emotion/react'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { MapItemsCache } from 'components/GoogleMap/MapItemsCache'
import type { ReactComponentOverlayView } from 'components/GoogleMap/_utils/makeReactComponentOverlayView'
import { makeReactComponentOverlayView } from 'components/GoogleMap/_utils/makeReactComponentOverlayView'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getPrimaryValueGroup } from 'stores/selectedValueGroupsStore/getPrimaryValueGroup'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import type { TIrrigationActivityForPointOrZone } from '../StationValuesCache/StationValuesCache'
import { IrrigationZoneValueBadgeComponent } from './IrrigationZoneValueBadgeComponent/IrrigationZoneValueBadgeComponent'

export type IRRIGATION_ZONE_VALUE_META = {
  irrigationZoneEmitterId: TIrrigationZoneEmitterId
  irrigationZoneName: string
  irrigationZoneEmitterType: TEmitterType
  irrigationZoneId: number
  propertyId: number
  bounds: google.maps.LatLngBounds
  latLngs: google.maps.LatLng[]
  latLng: google.maps.LatLng
  onClick: () => void
  irrigationActivity?: TIrrigationActivityForPointOrZone | null
}

// TODO: will eventually come from current values type
export type TIrrigationZonesValueTypesToPropsDictionary = Record<
  string,
  {
    children: React.ReactNode
    getContainerCSS: () => CSSObject
    progressText?: string
    progressPercent?: number
    baseZIndex: number
    valuesForZone: TIrrigationActivityForPointOrZone
  }
>

export class IrrigationZoneValuesCache extends MapItemsCache<
  IRRIGATION_ZONE_VALUE_META,
  TIrrigationZonesValueTypesToPropsDictionary,
  {
    badge: ReactComponentOverlayView<{
      meta: IRRIGATION_ZONE_VALUE_META
      valueTypesToPropsDictionary: TIrrigationZonesValueTypesToPropsDictionary
    }>
    polygon?: google.maps.Polygon
  }
> {
  constructor(options: { maps: typeof google.maps; map: google.maps.Map }) {
    super({
      createOverlay: (o) => {
        const polygon = hubConnectorSettings.enableIrrigationZonePolygons
          ? new o.maps.Polygon({ paths: o.meta.latLngs, map: o.map })
          : undefined

        polygon?.addListener('click', () => {
          setSelectedFieldAsset({
            irrigationZoneEmitter: o.meta.irrigationZoneEmitterId,
          })
        })

        return {
          polygon,
          badge: makeReactComponentOverlayView({
            component: IrrigationZoneValueBadgeComponent,
            latLng: o.meta.latLng,
            maps: o.maps,
            map: o.map,
          }),
        }
      },
      renderOverlay: (o) => {
        const isSelected =
          selectedFieldAssetsStore.getState().irrigationZoneEmitter === o.meta.irrigationZoneEmitterId

        const primaryValueGroup = getPrimaryValueGroup()
        const mapVisualValueGroup = mapControlsStore.getState().mapVisualValueGroup

        const valueType = !primaryValueGroup
          ? null
          : mapVisualValueGroup[MAP_VISUAL.IRRIGATION_ZONE]?.[primaryValueGroup]?.valueType

        o.overlay.polygon?.setOptions({
          map: o.map,
          strokeColor: isSelected ? 'yellow' : 'white',
          strokeWeight: isSelected ? 4 : 2,
          strokePosition: o.maps.StrokePosition.CENTER,
          fillColor: valueType
            ? String(o.value?.[valueType]?.getContainerCSS?.()?.['backgroundColor'])
            : null,
          fillOpacity: 0.3,
        })

        o.overlay.badge.render({ ...o, valueTypesToPropsDictionary: o.value })
      },
      hideOverlay: (o) => {
        o.overlay.polygon?.setMap(null)

        o.overlay.badge.setMap(null)
      },
      maps: options.maps,
      map: options.map,
    })
  }
}
